import { Injectable } from "@angular/core";
import { GlobalService } from "app/global/global.service";
import { TranslocoService } from "@jsverse/transloco";
import { Subject } from "rxjs";
import { BroadcastScope, PusherService, SUBSCRIPTION_ERROR_EVENT } from "app/global/pusher.service";
import { RoleDetail } from "app/main/signing-pages/signing-page/signing-page.dto";
import { fieldsValidation } from "app/global/fieldvalidation";
import { AuthenticationTypeEnum } from "app/main/signing-pages/enums/enum";
import { SingingPageService } from "app/main/signing-pages/signing-page/signing-page-service";

@Injectable({
    providedIn: 'root'
})

export class CriiptioBankService {
    private popupWindow: Window | null = null;
    private eidListener: { scope: BroadcastScope; channelName: string } | null = null;
    private bankIdSuccess$ = new Subject<void>();
    public authenticationTypeEnum = AuthenticationTypeEnum;

    constructor(
        private globalService: GlobalService,
        private translocoService: TranslocoService,
        protected singingPage: SingingPageService,
        private pusherService: PusherService,
    ) { }

   
    /**
     * Authenticates a user based on their role details and authentication type.
     *
     * Validates the provided fields and initiates the appropriate authentication flow
     * (Danish MitID or Norwegian BankID) depending on the user's authentication type.
     *
     * @param roleDetail - The details of the user's role, including authentication type and ID.
     * @param isEForm - Optional. Indicates if the authentication is for an eForm. Defaults to false.
     * @param id - Optional. An identifier used in the authentication process. Defaults to 0.
     * @returns Returns `true` if authentication is initiated, or `false` if validation fails.
     */
    authenticate(roleDetail: RoleDetail, isEForm: boolean = false, id: number = 0): boolean {
        const { status } = fieldsValidation(this.globalService.fields_data, this.globalService, String(roleDetail.id));
        if (!status) { return false }

        if (roleDetail.authentication_type === AuthenticationTypeEnum.DANISHMITID) {
            this.startDanishMitId(roleDetail, isEForm, id);
        } else if(roleDetail.authentication_type === AuthenticationTypeEnum.NORWEGIANBANKID) {
            this.startNorwegianBankId(roleDetail, isEForm, id);
        } else if(roleDetail.authentication_type === AuthenticationTypeEnum.FINNISHBANKID) {
            this.startFinnishBankId(roleDetail, isEForm, id);
        }
    }

    

    /**
     * Initiates the Danish MitID authentication process.
     *
     * @param roleDetail - The details of the role for which the BankID process is being initiated.
     * @param isEForm - A boolean indicating whether the process is for an electronic form.
     * @param id - The identifier for the specific entity or process.
     *
     * This method retrieves the authorization URL for the Norwegian BankID process and opens a popup
     * for the user to proceed with authentication. If the process fails, an error message is displayed
     * using a toast notification.
     */
    private startDanishMitId(roleDetail: RoleDetail, isEForm: boolean, id: number) {
        const bankIDName = this.translocoService.translate('Danish mitID addon title (addon modal) - Danish mitID');
        this.singingPage.getAuthzingsecMitIdUrl(roleDetail.id, id, isEForm)
            .subscribe({
                next: (response) => {
                    if (response?.status === 'success' && response?.data?.redirectUrl && response?.broadcast_channel) {
                        this.listenForEidResult(response.broadcast_channel, roleDetail, isEForm, id);
                        this.openPopup(response?.data?.redirectUrl, bankIDName);
                    } else {
                        this.globalService.toastrError(
                            this.translocoService.translate('Something went wrong (general toast error) - Something went wrong')
                        );
                    }
                },
                error: (error) => {
                    this.globalService.toastrError(error);
                },
            });
    }

    /**
     * Initiates the Norwegian BankID authentication process.
     *
     * @param roleDetail - The details of the role for which the BankID process is being initiated.
     * @param isEForm - A boolean indicating whether the process is for an electronic form.
     * @param id - The identifier for the specific entity or process.
     *
     * This method retrieves the authorization URL for the Norwegian BankID process and opens a popup
     * for the user to proceed with authentication. If the process fails, an error message is displayed
     * using a toast notification.
     */
    private startNorwegianBankId(roleDetail: RoleDetail, isEForm: boolean, id: number) {
        const bankIDName = this.translocoService.translate('Norwegian BankID modal addon title (addon modal) - Norwegian BankID');
        this.singingPage.getAuthzingsecUrl(roleDetail.id, id, isEForm)
            .subscribe({
                next: (response) => {
                    if (response?.status === 'success' && response?.data?.redirect_url && response?.broadcast_channel) {
                        this.listenForEidResult(response.broadcast_channel, roleDetail, isEForm, id);
                        this.openPopup(response?.data?.redirect_url, bankIDName);
                    } else {
                        this.globalService.toastrError(
                            this.translocoService.translate('Something went wrong (general toast error) - Something went wrong')
                        );
                    }
                },
                error: (error) => {
                    this.globalService.toastrError(error);
                },
            });
    }

    private startFinnishBankId(roleDetail: RoleDetail, isEForm: boolean, id: number) {
        const bankIDName = this.translocoService.translate('Finnish BankID modal addon title (addon modal) - Finnish BankID');
        this.singingPage.getAuthzingsecFinnishBankIdUrl(roleDetail.id, id, isEForm)
            .subscribe({
                next: (response) => {
                    if (response?.status === 'success' && response?.data?.redirect_url && response?.broadcast_channel) {
                        this.listenForEidResult(response.broadcast_channel, roleDetail, isEForm, id);
                        this.openPopup(response?.data?.redirect_url, bankIDName);
                    } else {
                        this.globalService.toastrError(
                            this.translocoService.translate('Something went wrong (general toast error) - Something went wrong')
                        );
                    }
                },
                error: (error) => {
                    this.globalService.toastrError(error);
                },
            });
    }

    /**
     * Listens for the result of one eID auth call on the private channel the backend minted for it,
     * then closes the popup and either reports the error or emits the identity on `bankIdSuccess$`.
     *
     * @param channelName - The `broadcast_channel` from the auth response.
     * @param roleDetail - The role being signed for.
     * @param isEForm - Whether the signer is filling an e-form, which authorizes with the template's token.
     * @param templateId - The e-form's template id; unused for documents.
     */
    private listenForEidResult(channelName: string, roleDetail: RoleDetail, isEForm: boolean, templateId: number) {
        this.stopListeningForEidResult();

        const scope: BroadcastScope = isEForm ? { kind: 'eform', templateId } : { kind: 'signer', roleId: roleDetail.id };
        const ID = isEForm ? roleDetail?.roleable_id : roleDetail?.id;
        const channel = this.pusherService.subscribe(scope, channelName);
        this.eidListener = { scope, channelName };

        // A refused channel would otherwise leave the popup open and the signer waiting for a result that never comes.
        channel.bind(SUBSCRIPTION_ERROR_EVENT, () => {
            this.stopListeningForEidResult();
            if (this.popupWindow) {
                this.popupWindow.close();
            }
            this.globalService.toastrError(
                this.translocoService.translate('Toaster error message for bankid (Signing page - section) - Unable to sign with BankID. Please try again')
            );
        });

        channel.bind(`bankid-callback-event-${ID}`, (data) => {
            this.stopListeningForEidResult();
            if (this.popupWindow) {
                this.popupWindow.close();
            }
            if (data?.params) {
                const params = data.params;
                if (params?.status === 'error') {
                    if (roleDetail.authentication_type === AuthenticationTypeEnum.DANISHMITID && params?.message) {
                        this.globalService.toastrError(params?.message);
                    } else {
                        this.globalService.toastrError(
                            this.translocoService.translate('Toaster error message for bankid (Signing page - section) - Unable to sign with BankID. Please try again')
                        );
                    }
                } else {
                    this.bankIdSuccess$.next(params);
                }
            }
        });
    }

    private stopListeningForEidResult() {
        if (this.eidListener) {
            this.pusherService.unsubscribe(this.eidListener.scope, this.eidListener.channelName);
            this.eidListener = null;
        }
    }

    /**
     * Returns an observable that emits the success events related to BankID operations.
     * 
     * @returns {Observable} An observable stream of BankID success events.
     */
    getBankIdSuccess$() {
        return this.bankIdSuccess$.asObservable();
    }

    /**
     * Opens a popup window with the specified URL and title, centered on the screen.
     *
     * @param url - The URL to be loaded in the popup window.
     * @param title - The title of the popup window.
     *
     * The method calculates the screen dimensions and system zoom to ensure the popup
     * is centered on the user's screen. The popup window is configured with scrollbars
     * enabled and a fixed width and height of 600 pixels (adjusted for system zoom).
     * If the browser supports the `focus` method, the popup window is brought to the front.
     */
    private openPopup(url: string, title: string) {
        const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
        const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

        const width = window.innerWidth || document.documentElement.clientWidth || screen.width;
        const height = window.innerHeight || document.documentElement.clientHeight || screen.height;

        const systemZoom = width / window.screen.availWidth;
        const left = (width - 600) / 2 / systemZoom + dualScreenLeft;
        const top = (height - 600) / 2 / systemZoom + dualScreenTop;

        this.popupWindow = window.open(
            url,
            title,
            `scrollbars=yes, width=${600 / systemZoom}, height=${600 / systemZoom
            }, top=${top}, left=${left}`
        );

        if (window?.focus) this.popupWindow?.focus();
    }
}